import { showToast, useAppSelector } from "@hypexone/uicore";
import { useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { isDesktop } from "react-device-detect";
import { SVLS_API_CLIENT } from "../../../App.utils";
import OpenBets from "../open-bets/OpenBets";
import Button from "../shared/Button/Button";
import UserMenuPageTitle from "../shared/user-menu/UserMenuPageTitle";
import moment from "moment";
import { getApkVersion, isApkOnlyBonusVisible } from "../../../utils/apkBonusVisibility";

type BonusRewardDto = {
  amount: number;
  category: string;
  claimed: boolean;
  reference_id: string;
  validity_date: string;
  description?: string;
  display_content?: string;
  only_apk?: boolean;
  min_apk_version?: string;
};

const BonusRewards = () => {
  const [loading, setLoading] = useState(false);
  const [bonuses, setBonuses] = useState<BonusRewardDto[]>([]);
  const [couponCode, setCouponCode] = useState("");
  const [bonusesLoading, setBonusesLoading] = useState(false);
  const { langData } = useAppSelector((state) => state.cms);
  const dispatch = useDispatch();

  useEffect(() => {
    getSpecialBonuses();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const errorToast = (message: string) => {
    dispatch(
      showToast({
        type: "error",
        message,
      })
    );
  };

  const successToast = (message: string) => {
    dispatch(
      showToast({
        type: "success",
        message,
      })
    );
  };

  const getSpecialBonuses = async () => {
    setBonusesLoading(true);
    try {
      const response = await SVLS_API_CLIENT.get(
        "/marketing/v1/bonuses/special-bonus",
        {
          headers: {
            Authorization: sessionStorage.getItem("jwt_token") || "",
          },
          params: {
            coupon_code: couponCode || null,
          },
        }
      );

      if (response.status === 200) {
        const validBonuses = (response.data || [])
          .filter((bonus: BonusRewardDto) =>
            moment(bonus.validity_date).isSameOrAfter(moment()) &&
            isApkOnlyBonusVisible(bonus)
          )
          .sort((a: BonusRewardDto, b: BonusRewardDto) =>
            moment(a.validity_date).isAfter(moment(b.validity_date)) ? -1 : 1
          );
        setBonuses(validBonuses);
      }
    } catch (error: any) {
      errorToast(error?.response?.data?.message || "Error fetching bonuses");
      setBonuses([]);
    } finally {
      setBonusesLoading(false);
    }
  };

  const claimSpecialBonus = async (bonusReferenceId: string) => {
    if (loading) return;

    setLoading(true);
    try {
      const response = await SVLS_API_CLIENT.post(
        "/marketing/v1/bonuses/claim-special-bonus2",
        null,
        {
          headers: {
            Authorization: sessionStorage.getItem("jwt_token") || "",
          },
          params: {
            reference_id: bonusReferenceId,
            is_apk: !!getApkVersion(),
          },
        }
      );

      if (response.status === 204) {
        successToast("Bonus Claimed Successfully");
        getSpecialBonuses();
      } else {
        errorToast("Failed to claim bonus");
      }
    } catch (error: any) {
      errorToast(error?.response?.data?.message || "Error claiming bonus");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex w-full">
      <div className="font-robotoCondensed border rounded-[4px] w-full flex-shrink-0">
        <div className="flex flex-col gap-2 p-2">
          <UserMenuPageTitle title={langData?.["redeem_bonus"] || "Claim Bonuses"} />

          <div className="flex gap-2 mb-3">
            <input
              type="text"
              className="text-black border border-gray-300 rounded px-3 py-2 w-full focus:outline-none focus:ring-1 focus:ring-blue-500"
              placeholder={langData?.["enter_coupon_code"] || "Enter coupon code"}
              value={couponCode}
              onChange={(e) => setCouponCode(e.target.value)}
            />
            <Button
              className="bg-blue4 text-white px-4 py-2 rounded text-sm font-semibold"
              onClick={getSpecialBonuses}
              loading={bonusesLoading}
            >
              {langData?.["search"] || "Search"}
            </Button>
          </div>

          {bonuses.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {bonuses.map((bonus) => (
                <div
                  key={bonus.reference_id}
                  className={`p-3 border rounded bg-white shadow-sm ${
                    bonus.claimed ? "opacity-70" : ""
                  }`}
                >
                  <h3 className="text-[14px] font-semibold text-black">
                    {bonus.description || langData?.[bonus.reference_id]?.heading}
                  </h3>
                  <p className="text-xs mt-2 text-gray-700">
                    {(langData?.["amount"] || "Amount") + ":"}{" "}
                    {Number(bonus.amount || 0).toFixed(2)}
                  </p>
                  <p className="text-xs mt-1 text-gray-700">
                    {(langData?.["expires_on"] || "Expires on") + ":"}{" "}
                    {moment(bonus.validity_date).format("DD/MM/YY")}
                  </p>

                  <div className="mt-2 text-xs text-gray-800 break-words">
                    {bonus.display_content ? (
                      <div
                        dangerouslySetInnerHTML={{ __html: bonus.display_content }}
                      />
                    ) : (
                      langData?.[bonus.reference_id]?.body || ""
                    )}
                  </div>

                  <Button
                    className={`mt-3 px-4 py-2 rounded text-white w-full text-xs font-semibold ${
                      bonus.claimed
                        ? "bg-gray-400 cursor-not-allowed"
                        : "bg-blue4 hover:bg-blue2"
                    }`}
                    onClick={() => claimSpecialBonus(bonus.reference_id)}
                    disabled={bonus.claimed || loading}
                  >
                    {bonus.claimed
                      ? langData?.["claimed"] || "Claimed"
                      : langData?.["claim_now"] || "Claim Now"}
                  </Button>
                </div>
              ))}
            </div>
          ) : (
            <div className="p-6 text-center text-sm text-gray-700 bg-gray-100 rounded">
              {langData?.["no_pending_bonus"] || "No pending bonus"}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default BonusRewards;

